(function() {
  'use strict';

  // Prevent double loading
  if (window.MegaMenuController) return;

  class MegaMenuController {
    constructor() {
      this.triggers = document.querySelectorAll('[data-mega-menu-trigger]');
      this.panels = document.querySelectorAll('.mm-overlay');
      this.header = document.querySelector('.header');
      this.shopifySectionHeader = document.querySelector('.shopify-section--header');
      
      this.hoverTimeout = null;
      this.activePanel = null;
      this.activeTrigger = null;
      this.isMouseInHeader = false;
      this.isMouseInPanel = false;

      // Configurations read from section dataset
      this.sectionElement = document.querySelector('.mm-section-data');
      this.hoverDelay = 500;
      this.closeOnScroll = true;
      this.headerSolidOnOpen = false;

      if (this.sectionElement) {
        this.hoverDelay = parseInt(this.sectionElement.dataset.hoverDelay, 10) || 500;
        this.closeOnScroll = this.sectionElement.dataset.closeOnScroll === 'true';
        this.headerSolidOnOpen = this.sectionElement.dataset.headerSolid === 'true';
      }

      this.init();
    }

    init() {
      this.setupMobileMenu();

      if (window.innerWidth < 1024) return; // Disable hover-intent listeners on tablet/mobile screens < 1024px

      this.setupEventListeners();
    }

    setupMobileMenu() {
      const sourcePanels = document.querySelectorAll('.mm-mobile-panels-source .header-sidebar__sub-panel');
      const targetScroller = document.querySelector('header-sidebar#sidebar-menu [slot="collapsible-panel"] .header-sidebar__scroller');
      
      if (!sourcePanels.length || !targetScroller) return;

      // Move sub-panels to the target collapsible container
      sourcePanels.forEach(panel => {
        if (!targetScroller.querySelector(`#${panel.id}`)) {
          targetScroller.appendChild(panel);
        }
      });

      // Find the main menu links in the sidebar
      const sidebarLinks = document.querySelectorAll('.header-sidebar__main-panel .header-sidebar__linklist a.header-sidebar__linklist-button');
      
      sidebarLinks.forEach(link => {
        const linkLabel = link.textContent.trim().toLowerCase();
        
        // Find if this link matches a mega menu panel
        let matchedPanel = null;
        let matchedBlockId = null;
        
        this.panels.forEach(panel => {
          if (panel.getAttribute('data-mm-label').trim().toLowerCase() === linkLabel) {
            matchedPanel = panel;
            matchedBlockId = panel.getAttribute('data-block-id');
          }
        });

        if (matchedBlockId) {
          const mobilePanelId = `header-panel-mega-${matchedBlockId}`;
          const mobilePanel = targetScroller.querySelector(`#${mobilePanelId}`);
          
          if (mobilePanel) {
            // Replace the <a> link with a <button type="button"> that acts as a disclosure trigger
            const triggerBtn = document.createElement('button');
            triggerBtn.type = 'button';
            triggerBtn.className = link.className;
            triggerBtn.setAttribute('aria-controls', mobilePanelId);
            triggerBtn.setAttribute('aria-expanded', 'false');
            triggerBtn.style.display = 'flex';
            triggerBtn.style.justifyContent = 'space-between';
            triggerBtn.style.alignItems = 'center';
            triggerBtn.style.width = '100%';
            triggerBtn.innerHTML = `
              <span>${link.textContent.trim()}</span>
              <svg role="presentation" class="icon icon-chevron-right" width="12" height="12" viewBox="0 0 12 12" fill="none" style="opacity: 0.7; transform: var(--transform-logical-flip);">
                <path d="M4 2L8 6L4 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            `;

            // Replace link with triggerBtn in DOM
            link.parentNode.replaceChild(triggerBtn, link);

            // Add click event listener to open the sub-panel
            triggerBtn.addEventListener('click', (e) => {
              const sidebar = document.querySelector('header-sidebar#sidebar-menu');
              const collapsiblePanel = sidebar.querySelector('[slot="collapsible-panel"]');
              
              if (sidebar && collapsiblePanel) {
                // Set active descendant which triggers the theme's default animation/view switch in Custom Element
                triggerBtn.setAttribute('aria-expanded', 'true');
                collapsiblePanel.setAttribute('aria-activedescendant', mobilePanelId);
                
                // Animate the main panel out (matching theme.js behavior)
                const mainPanel = sidebar.querySelector('.header-sidebar__main-panel');
                if (mainPanel) {
                  mainPanel.style.opacity = '0';
                  mainPanel.style.transform = 'translateX(-10px)';
                  mainPanel.style.transition = 'opacity 0.25s, transform 0.25s';
                }
              }
            });
          }
        }
      });

      // Handle the back button click to restore main panel animations
      const backButtons = targetScroller.querySelectorAll('.header-sidebar__back-button[data-action="close-panel"]');
      backButtons.forEach(btn => {
        btn.addEventListener('click', () => {
          const sidebar = document.querySelector('header-sidebar#sidebar-menu');
          const mainPanel = sidebar.querySelector('.header-sidebar__main-panel');
          if (mainPanel) {
            mainPanel.style.opacity = '';
            mainPanel.style.transform = '';
            mainPanel.style.transition = '';
          }
          
          // Reset aria-expanded on triggers
          const triggers = sidebar.querySelectorAll('[aria-controls^="header-panel-mega-"]');
          triggers.forEach(t => t.setAttribute('aria-expanded', 'false'));
        });
      });
    }

    setupEventListeners() {
      // Event delegation for triggers
      document.addEventListener('mouseenter', (e) => {
        const trigger = e.target.closest('[data-mega-menu-trigger]');
        if (!trigger) return;

        this.isMouseInHeader = true;
        const triggerLabel = trigger.getAttribute('data-mega-menu-trigger');
        const matchedPanel = this.findPanelByLabel(triggerLabel);

        if (matchedPanel) {
          // If hovering over another trigger while a panel is already active, open immediately without delay
          if (this.activePanel && this.activePanel !== matchedPanel) {
            this.switchPanel(matchedPanel, trigger);
          } else {
            this.scheduleOpen(matchedPanel, trigger);
          }
        } else {
          // Hovered a plain link with no mega menu, schedule close
          this.scheduleClose();
        }
      }, true);

      document.addEventListener('mouseleave', (e) => {
        const trigger = e.target.closest('[data-mega-menu-trigger]');
        if (!trigger) return;

        this.isMouseInHeader = false;
        this.scheduleClose();
      }, true);

      // Panel hover tracking
      this.panels.forEach(panel => {
        panel.addEventListener('mouseenter', () => {
          this.isMouseInPanel = true;
        });

        panel.addEventListener('mouseleave', () => {
          this.isMouseInPanel = false;
          this.scheduleClose();
        });
      });

      // Keep header hover tracked
      if (this.header) {
        this.header.addEventListener('mouseenter', () => {
          this.isMouseInHeader = true;
        });
        this.header.addEventListener('mouseleave', () => {
          this.isMouseInHeader = false;
          this.scheduleClose();
        });
      }

      // Close on click outside
      document.addEventListener('click', (e) => {
        if (!this.activePanel) return;
        
        const insideTrigger = e.target.closest('[data-mega-menu-trigger]');
        const insidePanel = e.target.closest('.mm-overlay');
        
        if (!insideTrigger && !insidePanel) {
          this.closeAllPanels();
        }
      });

      // Accessibility: Escape key and Focus/Tab trapping
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.activePanel) {
          this.closeAllPanels();
          if (this.activeTrigger) {
            this.activeTrigger.focus();
          }
        }
      });

      // Optional: Close on scroll
      if (this.closeOnScroll) {
        window.addEventListener('scroll', () => {
          if (this.activePanel) {
            this.closeAllPanels();
          }
        }, { passive: true });
      }

      // Re-initialize or handle resized screens
      window.addEventListener('resize', () => {
        if (window.innerWidth < 1024 && this.activePanel) {
          this.closeAllPanels();
        }
      });
    }

    findPanelByLabel(label) {
      if (!label) return null;
      const cleanLabel = label.trim().toLowerCase();
      let found = null;
      this.panels.forEach(panel => {
        if (panel.getAttribute('data-mm-label').trim().toLowerCase() === cleanLabel) {
          found = panel;
        }
      });
      return found;
    }

    scheduleOpen(panel, trigger) {
      this.clearTimeouts();
      this.hoverTimeout = setTimeout(() => {
        this.openPanel(panel, trigger);
      }, this.hoverDelay);
    }

    switchPanel(panel, trigger) {
      this.clearTimeouts();
      this.openPanel(panel, trigger);
    }

    scheduleClose() {
      this.clearTimeouts();
      this.hoverTimeout = setTimeout(() => {
        if (!this.isMouseInHeader && !this.isMouseInPanel) {
          this.closeAllPanels();
        }
      }, 100); // Small grace period
    }

    clearTimeouts() {
      if (this.hoverTimeout) {
        clearTimeout(this.hoverTimeout);
        this.hoverTimeout = null;
      }
    }

    openPanel(panel, trigger) {
      if (this.activePanel === panel) return;

      // Close previous panel first (if any)
      if (this.activePanel) {
        this.activePanel.classList.remove('is-active');
        if (this.activeTrigger) {
          this.activeTrigger.classList.remove('is-mm-active');
        }
      }

      this.activePanel = panel;
      this.activeTrigger = trigger;

      // Open new panel
      panel.classList.add('is-active');
      trigger.classList.add('is-mm-active');

      // Adjust positioning to sit exactly flush below the header element
      this.positionPanel(panel);

      // Active Header styling state if enabled in settings
      if (this.header && this.headerSolidOnOpen) {
        this.header.classList.add('is-mm-header-solid');
      }

      // Dispatch custom megamenu:open event
      document.dispatchEvent(new CustomEvent('megamenu:open', {
        detail: {
          label: trigger.getAttribute('data-mega-menu-trigger'),
          panel: panel
        }
      }));

      // Focus management inside the panel (first link or focusable element)
      const focusable = panel.querySelectorAll('a, button, input, [tabindex="0"]');
      if (focusable.length > 0) {
        // We set keyboard focus capability but don't force auto-focus immediately to prevent disruptive scroll jumping
        // Unless users tab into it.
      }
    }

    closeAllPanels() {
      if (!this.activePanel) return;

      const label = this.activeTrigger ? this.activeTrigger.getAttribute('data-mega-menu-trigger') : '';
      const closedPanel = this.activePanel;

      this.activePanel.classList.remove('is-active');
      
      if (this.activeTrigger) {
        this.activeTrigger.classList.remove('is-mm-active');
      }

      if (this.header && this.headerSolidOnOpen) {
        this.header.classList.remove('is-mm-header-solid');
      }

      this.activePanel = null;
      this.activeTrigger = null;

      // Dispatch custom megamenu:close event
      document.dispatchEvent(new CustomEvent('megamenu:close', {
        detail: {
          label: label,
          panel: closedPanel
        }
      }));
    }

    positionPanel(panel) {
      if (!this.header) return;
      
      // Use the header's live viewport position so the fixed panel
      // always appears directly below the visible (sticky) header
      const headerRect = this.header.getBoundingClientRect();
      const topVal = headerRect.bottom;
      
      panel.style.top = `${topVal}px`;
      // Also set the CSS custom property for max-height calculation
      panel.style.setProperty('--mm-panel-top', `${topVal}px`);
    }
  }

  // Load on DOMContentLoaded or immediately if ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.MegaMenuController = new MegaMenuController();
    });
  } else {
    window.MegaMenuController = new MegaMenuController();
  }

  // Hook Shopify Theme Editor select/deselect block events
  document.addEventListener('shopify:block:select', (e) => {
    const blockId = e.detail.blockId;
    const panel = document.querySelector(`.mm-overlay[data-block-id="${blockId}"]`);
    if (panel) {
      const label = panel.getAttribute('data-mm-label');
      const trigger = document.querySelector(`[data-mega-menu-trigger="${label}"]`);
      if (trigger && window.MegaMenuController) {
        window.MegaMenuController.openPanel(panel, trigger);
      }
    }
  });

  document.addEventListener('shopify:block:deselect', (e) => {
    if (window.MegaMenuController) {
      window.MegaMenuController.closeAllPanels();
    }
  });

})();
